import React, { useEffect, useMemo, useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Box,
    Typography,
    Stack,
    Alert,
    Chip,
    CircularProgress,
    FormControlLabel,
    FormGroup,
    FormLabel,
    FormHelperText,
    Checkbox,
    RadioGroup,
    Radio,
    Switch,
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import { rosterService, Shift } from '../../services/rosterService';
import { staffService } from '../../services/staffService';
import {
    billingRatesService,
    staffBaseRateService,
    clientBaseRateService,
    flatRateService,
    BillingRate,
    FlatRate,
} from '../../services/newBillingService';
import { getStaffDisplayName, getClientDisplayName } from '../../utils/nameUtils';
import { formatDateTimeForApi } from '../../utils/dateUtils';
import { computeShiftRateBreakdown, RateBreakdown } from '../../utils/shiftRateCalc';

export interface ShiftFormDialogProps {
    open: boolean;
    onClose: () => void;
    /** Existing shift to edit. Pass null/undefined for create. */
    shift?: Shift | null;
    /** Pre-filled start datetime when opening from a calendar day click. */
    initialStart?: string;
    /** Pre-filled end datetime when opening from a calendar day click. */
    initialEnd?: string;
    /** Pre-filled staff (e.g. when calendar is filtered by staff). */
    initialStaffId?: number;
    /** Rich client list — items must include at least { id, firstName?, lastName?, fullName?, ... }. */
    clients: any[];
    /** Rich staff list — items must include at least { id, firstName?, lastName?, employeeId?, ... }. */
    staff: any[];
    /**
     * Treated as "admin" for the availability gate. When true, a non-available
     * staff/time combination will warn but not block save.
     */
    canEditShifts?: boolean;
    /** Called after a successful create/update so the parent can refresh. */
    onSaved?: () => void;
}

const ShiftFormDialog: React.FC<ShiftFormDialogProps> = ({
    open,
    onClose,
    shift,
    initialStart,
    initialEnd,
    initialStaffId,
    clients,
    staff,
    canEditShifts = true,
    onSaved,
}) => {
    // ── Form state ─────────────────────────────────────────────────────────
    const blankForm: Partial<Shift> = {
        shiftType: 'regular',
        status: 'scheduled',
        hourlyRate: 0,
        clientId: 0,
        staffId: 0,
        startDateTime: dayjs().format('YYYY-MM-DDTHH:mm:ss'),
        endDateTime: dayjs().add(4, 'hour').format('YYYY-MM-DDTHH:mm:ss'),
        isRecurring: false,
        ...({
            creationType: 'one-off',
            recurrenceInterval: 1,
            daysOfWeek: '',
            maxOccurrences: 10,
            endCondition: 'endDate',
        } as any),
    };

    const [formData, setFormData] = useState<Partial<Shift>>(blankForm);
    const [error, setError] = useState<string | null>(null);
    const [saving, setSaving] = useState(false);

    const [staffAvailability, setStaffAvailability] = useState<{
        isAvailable: boolean;
        conflicts: string[];
        availability: any;
    } | null>(null);
    const [availabilityLoading, setAvailabilityLoading] = useState(false);
    const [selectedStaffProfile, setSelectedStaffProfile] = useState<any>(null);
    const [calculatedDuration, setCalculatedDuration] = useState<number>(0);

    // ── Rate Preview state ─────────────────────────────────────────────────
    const [staffPaySegments, setStaffPaySegments] = useState<BillingRate[]>([]);
    const [clientChargeSegments, setClientChargeSegments] = useState<BillingRate[]>([]);
    const [staffSleepoverFlat, setStaffSleepoverFlat] = useState<FlatRate | null>(null);
    const [clientSleepoverFlat, setClientSleepoverFlat] = useState<FlatRate | null>(null);
    const [previewStaffBaseRate, setPreviewStaffBaseRate] = useState<number>(0);
    const [previewClientBaseRate, setPreviewClientBaseRate] = useState<number>(0);

    const isEditing = !!(shift && (shift as any).id);

    // ── Reset form when opening, or when shift changes ─────────────────────
    useEffect(() => {
        if (!open) return;

        if (shift && (shift as any).id) {
            const fd: Partial<Shift> = {
                ...shift,
                startDateTime: shift.startDateTime || dayjs().format('YYYY-MM-DDTHH:mm:ss'),
                endDateTime: shift.endDateTime || dayjs().add(4, 'hour').format('YYYY-MM-DDTHH:mm:ss'),
            };
            (fd as any).creationType = shift.isRecurring || shift.parentShiftId || shift.isRecurrenceParent
                ? 'recurring' : 'one-off';
            (fd as any).recurrenceType = shift.recurrenceType;
            (fd as any).recurrenceInterval = shift.recurrenceInterval || 1;
            (fd as any).daysOfWeek = shift.daysOfWeek || '';
            (fd as any).dayOfMonth = shift.dayOfMonth;
            (fd as any).recurrenceEndDate = shift.recurrenceEndDate || '';
            (fd as any).maxOccurrences = shift.maxOccurrences || 10;
            (fd as any).endCondition = shift.recurrenceEndDate ? 'endDate' : 'maxOccurrences';
            setFormData(fd);
        } else {
            setFormData({
                ...blankForm,
                staffId: initialStaffId ?? 0,
                startDateTime: initialStart ?? blankForm.startDateTime,
                endDateTime: initialEnd ?? blankForm.endDateTime,
            });
        }
        setError(null);
        setStaffAvailability(null);
        setSelectedStaffProfile(null);
        setCalculatedDuration(0);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, shift, initialStart, initialEnd, initialStaffId]);

    // ── Load Billing Rate config once ──────────────────────────────────────
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const today = new Date();
                const isActiveFlat = (r: FlatRate) => r.isActive
                    && new Date(r.effectiveFrom) <= today
                    && (!r.effectiveTo || new Date(r.effectiveTo) >= today);

                const [staffPay, clientCharge, staffFlats, clientFlats] = await Promise.all([
                    billingRatesService.getAllRates('StaffPay'),
                    billingRatesService.getAllRates('ClientCharge'),
                    flatRateService.getAll(undefined, 'StaffPay'),
                    flatRateService.getAll(undefined, 'ClientCharge'),
                ]);
                if (cancelled) return;
                if (staffPay.success && staffPay.data) setStaffPaySegments(staffPay.data.filter(r => r.isActive));
                if (clientCharge.success && clientCharge.data) setClientChargeSegments(clientCharge.data.filter(r => r.isActive));
                if (staffFlats.success && staffFlats.data) {
                    setStaffSleepoverFlat(staffFlats.data.find(r => r.category === 'Sleepover' && isActiveFlat(r)) ?? null);
                }
                if (clientFlats.success && clientFlats.data) {
                    setClientSleepoverFlat(clientFlats.data.find(r => r.category === 'Sleepover' && isActiveFlat(r)) ?? null);
                }
            } catch (err) {
                console.warn('Failed to load Billing Rate config — Rate Preview will be unavailable.', err);
            }
        })();
        return () => { cancelled = true; };
    }, []);

    // ── Resolve StaffBaseRate when staff selection changes ─────────────────
    useEffect(() => {
        const sid = formData.staffId;
        if (!sid) { setPreviewStaffBaseRate(0); return; }
        let cancelled = false;
        staffBaseRateService.getActiveForStaff(sid)
            .then(res => {
                if (cancelled) return;
                const rate = res?.success && res.data?.baseHourlyRate != null
                    ? Number(res.data.baseHourlyRate) : 0;
                setPreviewStaffBaseRate(rate);
            })
            .catch(() => { if (!cancelled) setPreviewStaffBaseRate(0); });
        return () => { cancelled = true; };
    }, [formData.staffId]);

    // ── Resolve ClientBaseRate when client selection changes ───────────────
    useEffect(() => {
        const cid = formData.clientId;
        if (!cid) { setPreviewClientBaseRate(0); return; }
        let cancelled = false;
        clientBaseRateService.getForClient(cid)
            .then(res => {
                if (cancelled) return;
                const rates = res?.success && res.data ? res.data : [];
                const today = new Date();
                const active = rates.find(r => r.isActive
                    && r.serviceType === 'PersonalCare'
                    && new Date(r.effectiveFrom) <= today
                    && (!r.effectiveTo || new Date(r.effectiveTo) >= today));
                setPreviewClientBaseRate(active ? Number(active.baseHourlyRate) : 0);
            })
            .catch(() => { if (!cancelled) setPreviewClientBaseRate(0); });
        return () => { cancelled = true; };
    }, [formData.clientId]);

    // ── Live availability check whenever staff/time changes ────────────────
    useEffect(() => {
        if (formData.staffId && formData.startDateTime && formData.endDateTime) {
            checkAvailability(formData.staffId, formData.startDateTime, formData.endDateTime);
        } else {
            setStaffAvailability(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formData.staffId, formData.startDateTime, formData.endDateTime]);

    const checkAvailability = async (staffId: number, start: string, end: string) => {
        if (!staffId || !start || !end) { setStaffAvailability(null); return; }
        try {
            setAvailabilityLoading(true);
            let availability: any[] = [];
            try {
                availability = await staffService.getStaffAvailability(staffId);
            } catch {
                availability = [
                    { dayOfWeek: 'monday', isAvailable: true, startTime: '09:00:00', endTime: '17:00:00' },
                    { dayOfWeek: 'tuesday', isAvailable: true, startTime: '09:00:00', endTime: '17:00:00' },
                    { dayOfWeek: 'wednesday', isAvailable: true, startTime: '09:00:00', endTime: '17:00:00' },
                    { dayOfWeek: 'thursday', isAvailable: true, startTime: '09:00:00', endTime: '17:00:00' },
                    { dayOfWeek: 'friday', isAvailable: true, startTime: '09:00:00', endTime: '17:00:00' },
                    { dayOfWeek: 'saturday', isAvailable: false, startTime: '00:00:00', endTime: '00:00:00' },
                    { dayOfWeek: 'sunday', isAvailable: false, startTime: '00:00:00', endTime: '00:00:00' },
                ];
            }

            const startDate = dayjs(start);
            const endDate = dayjs(end);
            const dayOfWeek = startDate.format('dddd').toLowerCase();
            const dayAvail = availability.find((a: any) => a.dayOfWeek.toLowerCase() === dayOfWeek && a.isAvailable);

            let isAvailable = true;
            const conflicts: string[] = [];
            if (!dayAvail) {
                isAvailable = false;
                conflicts.push(`Staff is not available on ${startDate.format('dddd')}s`);
            } else {
                const shiftStart = startDate.format('HH:mm:ss');
                const shiftEnd = endDate.format('HH:mm:ss');
                if (shiftStart < dayAvail.startTime || shiftEnd > dayAvail.endTime) {
                    isAvailable = false;
                    conflicts.push(`Shift time (${startDate.format('HH:mm')} - ${endDate.format('HH:mm')}) is outside available hours (${dayAvail.startTime.slice(0, 5)} - ${dayAvail.endTime.slice(0, 5)})`);
                }
            }

            setStaffAvailability({ isAvailable, conflicts, availability: dayAvail });
        } catch (err: any) {
            setStaffAvailability({
                isAvailable: true,
                conflicts: [`Unable to verify availability: ${err?.message || 'Unknown error'}`],
                availability: null,
            });
        } finally {
            setAvailabilityLoading(false);
        }
    };

    // Auto-update duration when start/end changes.
    useEffect(() => {
        if (formData.startDateTime && formData.endDateTime) {
            const s = dayjs(formData.startDateTime);
            const e = dayjs(formData.endDateTime);
            if (e.isAfter(s)) {
                setCalculatedDuration(e.diff(s, 'hour', true));
                return;
            }
        }
        setCalculatedDuration(0);
    }, [formData.startDateTime, formData.endDateTime]);

    // Fetch staff profile for the availability panel display name.
    useEffect(() => {
        const sid = formData.staffId;
        if (!sid) { setSelectedStaffProfile(null); return; }
        let cancelled = false;
        staffService.getStaffProfile(sid)
            .then(p => { if (!cancelled) setSelectedStaffProfile(p); })
            .catch(() => { if (!cancelled) setSelectedStaffProfile(null); });
        return () => { cancelled = true; };
    }, [formData.staffId]);

    // ── Live Rate Preview ──────────────────────────────────────────────────
    const isSleepover = (formData as any).isSleepover === true || formData.shiftType === 'sleepover';

    const ratePreview: { staff: RateBreakdown; client: RateBreakdown } = useMemo(() => {
        const opts = {
            start: formData.startDateTime || null,
            end: formData.endDateTime || null,
            isSleepover,
            travelKm: 0,
        };
        return {
            staff: computeShiftRateBreakdown({
                ...opts,
                baseRate: previewStaffBaseRate,
                segments: staffPaySegments,
                sleepoverFlatRate: staffSleepoverFlat,
                travelFlatRate: null,
            }),
            client: computeShiftRateBreakdown({
                ...opts,
                baseRate: previewClientBaseRate,
                segments: clientChargeSegments,
                sleepoverFlatRate: clientSleepoverFlat,
                travelFlatRate: null,
            }),
        };
    }, [formData.startDateTime, formData.endDateTime, isSleepover,
        previewStaffBaseRate, previewClientBaseRate,
        staffPaySegments, clientChargeSegments,
        staffSleepoverFlat, clientSleepoverFlat]);

    const fmtCurrency = (n: number) =>
        new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format(n);

    // ── Form change handler ────────────────────────────────────────────────
    const handleChange = (field: string, value: any) => {
        setFormData(prev => {
            const updated: any = { ...prev, [field]: value };
            if (field === 'isRecurring') {
                updated.creationType = value ? 'recurring' : 'one-off';
                if (value) {
                    updated.recurrenceType = updated.recurrenceType || 'weekly';
                    updated.recurrenceInterval = updated.recurrenceInterval || 1;
                    updated.daysOfWeek = updated.daysOfWeek || '';
                    updated.maxOccurrences = updated.maxOccurrences || 10;
                    updated.endCondition = updated.endCondition || 'maxOccurrences';
                }
            }
            return updated;
        });
    };

    const handleDateTimeChange = (field: 'startDateTime' | 'endDateTime', value: any) => {
        try {
            if (value && dayjs.isDayjs(value) && value.isValid()) {
                handleChange(field, value.format('YYYY-MM-DDTHH:mm:ss'));
            } else {
                handleChange(field, null);
            }
        } catch {
            handleChange(field, null);
        }
    };

    // ── Save ────────────────────────────────────────────────────────────────
    const handleSave = async () => {
        try {
            if (!formData.clientId) { setError('Please select a client'); return; }
            if (!formData.staffId) { setError('Please select a staff member'); return; }
            if (!formData.startDateTime) { setError('Please select a start date and time'); return; }
            if (!formData.endDateTime) { setError('Please select an end date and time'); return; }

            const startTime = dayjs(formData.startDateTime);
            const endTime = dayjs(formData.endDateTime);
            if (endTime.isBefore(startTime) || endTime.isSame(startTime)) {
                setError('End time must be after start time');
                return;
            }
            if (staffAvailability && !staffAvailability.isAvailable && !canEditShifts) {
                setError('Selected staff is not available during the requested time. Please choose a different time or contact an administrator.');
                return;
            }

            const isCancelled = formData.status === 'cancelled';
            if (isCancelled) {
                if (formData.cancellationChargePercent === null
                    || formData.cancellationChargePercent === undefined) {
                    setError('Cancelled shifts require a client charge selection (None / 50% / 100%).');
                    return;
                }
                if (formData.payStaffForCancellation === null
                    || formData.payStaffForCancellation === undefined) {
                    setError('Cancelled shifts require a staff-pay decision (Pay or Do not pay).');
                    return;
                }
            }

            const isRecurring = (formData as any).creationType === 'recurring';
            const payload: any = {
                ...formData,
                startDateTime: formatDateTimeForApi(formData.startDateTime),
                endDateTime: formatDateTimeForApi(formData.endDateTime),
                hourlyRate: 0,                              // ← obsolete; kept for required column
                clientId: Number(formData.clientId),
                staffId: Number(formData.staffId),
                isRecurring,
                // Cancellation flags only travel when status === 'cancelled';
                // the backend nulls them out for any other status anyway.
                cancellationChargePercent: isCancelled ? formData.cancellationChargePercent : null,
                payStaffForCancellation: isCancelled ? formData.payStaffForCancellation : null,
                ...(isRecurring && {
                    recurrenceType: (formData as any).recurrenceType,
                    recurrenceInterval: Number((formData as any).recurrenceInterval) || 1,
                    daysOfWeek: (formData as any).daysOfWeek,
                    dayOfMonth: (formData as any).dayOfMonth ? Number((formData as any).dayOfMonth) : undefined,
                    recurrenceEndDate: (formData as any).endCondition === 'endDate'
                        ? formatDateTimeForApi((formData as any).recurrenceEndDate)
                        : undefined,
                    maxOccurrences: (formData as any).endCondition === 'maxOccurrences'
                        ? Number((formData as any).maxOccurrences)
                        : undefined,
                }),
            };

            setSaving(true);
            setError(null);
            if (isEditing && shift?.id) {
                const editChoice = (shift as any).editChoice;
                if (editChoice && (shift.parentShiftId || shift.isRecurrenceParent)) {
                    if (editChoice === '1') {
                        await rosterService.updateShift(shift.id, payload);
                    } else if (editChoice === '2') {
                        const parentId = shift.parentShiftId || shift.id;
                        const fromDate = (shift.startDateTime || '').split('T')[0];
                        await rosterService.updateRecurringShiftsFrom(parentId!, fromDate, payload);
                    }
                } else {
                    await rosterService.updateShift(shift.id, payload);
                }
            } else {
                await rosterService.createShift(payload);
            }
            onSaved?.();
            onClose();
        } catch (err: any) {
            setError(err?.response?.data?.message || (isEditing ? 'Failed to update shift' : 'Failed to create shift'));
        } finally {
            setSaving(false);
        }
    };

    // ── Render ──────────────────────────────────────────────────────────────
    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>{isEditing ? 'Edit Shift' : 'Add New Shift'}</DialogTitle>
            <DialogContent>
                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

                <Stack spacing={3} sx={{ mt: 2 }}>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <FormControl fullWidth>
                            <InputLabel>Shift Creation Type</InputLabel>
                            <Select
                                value={(formData as any).isRecurring ? 'recurring' : 'one-off'}
                                onChange={(e) => handleChange('isRecurring', e.target.value === 'recurring')}
                                label="Shift Creation Type"
                            >
                                <MenuItem value="one-off">One-off Shift</MenuItem>
                                <MenuItem value="recurring">Recurring Shift</MenuItem>
                            </Select>
                        </FormControl>
                    </Box>

                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <FormControl fullWidth required error={!formData.clientId}>
                            <InputLabel>Client</InputLabel>
                            <Select
                                value={formData.clientId || ''}
                                onChange={(e) => handleChange('clientId', e.target.value)}
                            >
                                {clients.length === 0 ? (
                                    <MenuItem disabled>No clients available</MenuItem>
                                ) : (
                                    clients.map((c) => (
                                        <MenuItem key={c.id} value={c.id}>
                                            {getClientDisplayName(c)}
                                        </MenuItem>
                                    ))
                                )}
                            </Select>
                        </FormControl>
                        <FormControl fullWidth required error={!formData.staffId}>
                            <InputLabel>Staff</InputLabel>
                            <Select
                                value={formData.staffId || ''}
                                onChange={(e) => handleChange('staffId', e.target.value)}
                            >
                                {staff.filter(s => s.name || s.firstName).length === 0 ? (
                                    <MenuItem disabled>No staff available</MenuItem>
                                ) : (
                                    staff.filter(s => s.name || s.firstName).map((s) => (
                                        <MenuItem key={s.id} value={s.id}>
                                            {getStaffDisplayName(s)}
                                        </MenuItem>
                                    ))
                                )}
                            </Select>
                        </FormControl>
                    </Box>

                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <FormControl fullWidth>
                            <InputLabel>Shift Type</InputLabel>
                            <Select
                                value={formData.shiftType || 'regular'}
                                onChange={(e) => handleChange('shiftType', e.target.value)}
                                label="Shift Type"
                            >
                                <MenuItem value="regular">Regular</MenuItem>
                                <MenuItem value="respite">Respite</MenuItem>
                                <MenuItem value="emergency">Emergency</MenuItem>
                                <MenuItem value="overnight">Overnight</MenuItem>
                                <MenuItem value="sleepover">Sleepover (Inactive Overnight)</MenuItem>
                            </Select>
                        </FormControl>
                        <FormControl fullWidth>
                            <InputLabel>Status</InputLabel>
                            <Select
                                value={formData.status || 'scheduled'}
                                onChange={(e) => {
                                    const next = e.target.value as Shift['status'];
                                    setFormData(prev => ({
                                        ...prev,
                                        status: next,
                                        // Clear cancellation flags whenever the
                                        // status moves away from 'cancelled' so
                                        // stale values don't get persisted.
                                        ...(next === 'cancelled'
                                            ? {}
                                            : { cancellationChargePercent: null, payStaffForCancellation: null }),
                                    }));
                                }}
                                label="Status"
                            >
                                <MenuItem value="scheduled">Scheduled</MenuItem>
                                <MenuItem value="in_progress">In Progress</MenuItem>
                                <MenuItem value="pending_review">Pending Review</MenuItem>
                                <MenuItem value="completed">Completed</MenuItem>
                                <MenuItem value="cancelled">Cancelled</MenuItem>
                                <MenuItem value="no_show">No Show</MenuItem>
                            </Select>
                        </FormControl>
                    </Box>

                    {formData.status === 'cancelled' && (
                        <Box
                            sx={{
                                p: 2,
                                border: '1px solid',
                                borderColor: 'warning.light',
                                borderRadius: 1,
                                bgcolor: 'warning.lighter',
                            }}
                        >
                            <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
                                Cancellation billing
                            </Typography>
                            <Stack spacing={2}>
                                <FormControl
                                    required
                                    error={
                                        formData.cancellationChargePercent === null
                                        || formData.cancellationChargePercent === undefined
                                    }
                                >
                                    <FormLabel>Client charge</FormLabel>
                                    <RadioGroup
                                        row
                                        value={
                                            formData.cancellationChargePercent === null
                                            || formData.cancellationChargePercent === undefined
                                                ? ''
                                                : String(formData.cancellationChargePercent)
                                        }
                                        onChange={(e) =>
                                            handleChange('cancellationChargePercent', Number(e.target.value))
                                        }
                                    >
                                        <FormControlLabel value="0" control={<Radio />} label="No charge" />
                                        <FormControlLabel value="50" control={<Radio />} label="50%" />
                                        <FormControlLabel value="100" control={<Radio />} label="100%" />
                                    </RadioGroup>
                                    <FormHelperText>
                                        Drives the Cancellation Fee line on the client invoice.
                                    </FormHelperText>
                                </FormControl>

                                <FormControl
                                    required
                                    error={
                                        formData.payStaffForCancellation === null
                                        || formData.payStaffForCancellation === undefined
                                    }
                                >
                                    <FormLabel>Pay staff for this shift?</FormLabel>
                                    <RadioGroup
                                        row
                                        value={
                                            formData.payStaffForCancellation === null
                                            || formData.payStaffForCancellation === undefined
                                                ? ''
                                                : formData.payStaffForCancellation ? 'yes' : 'no'
                                        }
                                        onChange={(e) =>
                                            handleChange('payStaffForCancellation', e.target.value === 'yes')
                                        }
                                    >
                                        <FormControlLabel value="yes" control={<Radio />} label="Pay" />
                                        <FormControlLabel value="no" control={<Radio />} label="Do not pay" />
                                    </RadioGroup>
                                    <FormHelperText>
                                        When paid, the staff is paid the same percent of the scheduled shift as the client is charged.
                                    </FormHelperText>
                                </FormControl>
                            </Stack>
                        </Box>
                    )}

                    {/* Sleepover toggle — also flips shiftType for backend filters/reports */}
                    <Box>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={isSleepover}
                                    onChange={(e) => {
                                        const v = e.target.checked;
                                        setFormData(prev => ({
                                            ...prev,
                                            ...(({ isSleepover: v } as any)),
                                            shiftType: v ? 'sleepover' : (prev.shiftType === 'sleepover' ? 'regular' : prev.shiftType),
                                            // Auto-tag the NDIS service category when toggling sleepover
                                            ...(v ? { serviceCategory: 'Sleepover' } : {}),
                                        } as any));
                                    }}
                                />
                            }
                            label="Sleepover (flat per-night rate)"
                        />
                    </Box>

                    {/* NDIS service classification (per-shift). Drives the
                        Support Item Number + Claim Type stamped on each
                        invoice line. */}
                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <FormControl fullWidth>
                            <InputLabel>NDIS Service Category</InputLabel>
                            <Select
                                value={(formData as any).serviceCategory ?? 'PersonalCare'}
                                onChange={(e) => handleChange('serviceCategory' as any, e.target.value)}
                                label="NDIS Service Category"
                            >
                                <MenuItem value="PersonalCare">Personal Care (Assistance with Daily Living)</MenuItem>
                                <MenuItem value="HomeHelp">Home Help (Domestic Assistance)</MenuItem>
                                <MenuItem value="CommunityAccess">Community Access &amp; Social Participation</MenuItem>
                                <MenuItem value="Sleepover">Sleepover (Inactive Overnight)</MenuItem>
                                <MenuItem value="Transport">Provider Travel</MenuItem>
                            </Select>
                        </FormControl>
                        <FormControl fullWidth>
                            <InputLabel>NDIS Claim Type</InputLabel>
                            <Select
                                value={(formData as any).claimType ?? 'Face-to-face support'}
                                onChange={(e) => handleChange('claimType' as any, e.target.value)}
                                label="NDIS Claim Type"
                            >
                                <MenuItem value="Face-to-face support">Face-to-face support (default)</MenuItem>
                                <MenuItem value="Provider travel">Provider travel (TRAN)</MenuItem>
                                <MenuItem value="Non face-to-face support">Non face-to-face support (NF2F)</MenuItem>
                                <MenuItem value="NDIA requested report">NDIA requested report (REPW)</MenuItem>
                                <MenuItem value="Irregular SIL support">Irregular SIL support (SIL)</MenuItem>
                                <MenuItem value="Telehealth">Telehealth (TH)</MenuItem>
                                <MenuItem value="Short notice cancellation">Short notice cancellation</MenuItem>
                            </Select>
                        </FormControl>
                    </Box>

                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <DateTimePicker
                            label="Start Date & Time"
                            value={formData.startDateTime ? dayjs(formData.startDateTime) : null}
                            onChange={(value) => handleDateTimeChange('startDateTime', value)}
                            views={['year', 'month', 'day', 'hours', 'minutes']}
                            ampm
                            sx={{ flex: 1 }}
                            slotProps={{ textField: { helperText: 'Select any start date and time' } }}
                        />
                        <DateTimePicker
                            label="End Date & Time"
                            value={formData.endDateTime ? dayjs(formData.endDateTime) : null}
                            onChange={(value) => handleDateTimeChange('endDateTime', value)}
                            minDateTime={formData.startDateTime ? dayjs(formData.startDateTime) : undefined}
                            views={['year', 'month', 'day', 'hours', 'minutes']}
                            ampm
                            sx={{ flex: 1 }}
                            slotProps={{
                                textField: {
                                    error: !!(formData.startDateTime && formData.endDateTime
                                        && (dayjs(formData.endDateTime).isBefore(dayjs(formData.startDateTime))
                                            || dayjs(formData.endDateTime).isSame(dayjs(formData.startDateTime)))),
                                    helperText: formData.startDateTime && formData.endDateTime
                                        ? (dayjs(formData.endDateTime).isBefore(dayjs(formData.startDateTime))
                                            || dayjs(formData.endDateTime).isSame(dayjs(formData.startDateTime))
                                            ? 'End time must be after start time' : 'Valid end time')
                                        : 'Select end date and time',
                                },
                            }}
                        />
                    </Box>

                    {/* Recurring options */}
                    {formData.isRecurring && (
                        <Box sx={{ p: 2, border: 1, borderColor: 'primary.main', borderRadius: 1, backgroundColor: 'primary.50' }}>
                            <Typography variant="subtitle2" sx={{ mb: 1, color: 'primary.main' }}>🔄 Recurring Shift Settings</Typography>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <Box sx={{ display: 'flex', gap: 2 }}>
                                    <FormControl sx={{ flex: 1 }}>
                                        <InputLabel>Frequency</InputLabel>
                                        <Select
                                            value={(formData as any).recurrenceType || ''}
                                            onChange={(e) => setFormData({ ...formData, recurrenceType: e.target.value as any })}
                                            label="Frequency"
                                        >
                                            <MenuItem value="daily">Daily</MenuItem>
                                            <MenuItem value="weekly">Weekly</MenuItem>
                                            <MenuItem value="monthly">Monthly</MenuItem>
                                        </Select>
                                    </FormControl>
                                    <TextField
                                        sx={{ flex: 1 }}
                                        type="number"
                                        label="Interval"
                                        value={(formData as any).recurrenceInterval || 1}
                                        onChange={(e) => setFormData({ ...formData, recurrenceInterval: parseInt(e.target.value) || 1 } as any)}
                                        inputProps={{ min: 1, max: 12 }}
                                    />
                                </Box>
                                {(formData as any).recurrenceType === 'weekly' && (
                                    <FormControl component="fieldset">
                                        <FormLabel component="legend">Days of Week</FormLabel>
                                        <FormGroup row>
                                            {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map((day) => {
                                                const sel = (formData.daysOfWeek || '').split(',').filter(Boolean);
                                                return (
                                                    <FormControlLabel
                                                        key={day}
                                                        control={
                                                            <Checkbox
                                                                checked={sel.includes(day)}
                                                                onChange={(e) => {
                                                                    const next = e.target.checked
                                                                        ? [...sel, day]
                                                                        : sel.filter(d => d !== day);
                                                                    setFormData({ ...formData, daysOfWeek: next.join(',') });
                                                                }}
                                                            />
                                                        }
                                                        label={day.slice(0, 3)}
                                                    />
                                                );
                                            })}
                                        </FormGroup>
                                        <FormHelperText>Select which days the shift should repeat.</FormHelperText>
                                    </FormControl>
                                )}
                                {(formData as any).recurrenceType === 'monthly' && (
                                    <TextField
                                        sx={{ width: '200px' }}
                                        type="number"
                                        label="Day of Month"
                                        value={(formData as any).dayOfMonth || (formData.startDateTime ? dayjs(formData.startDateTime).date() : 1)}
                                        onChange={(e) => setFormData({ ...formData, dayOfMonth: parseInt(e.target.value) || 1 } as any)}
                                        inputProps={{ min: 1, max: 31 }}
                                    />
                                )}
                                <FormControl component="fieldset">
                                    <FormLabel component="legend">End condition</FormLabel>
                                    <RadioGroup
                                        row
                                        value={(formData as any).endCondition || 'endDate'}
                                        onChange={(e) => setFormData({ ...formData, endCondition: e.target.value } as any)}
                                    >
                                        <FormControlLabel value="endDate" control={<Radio />} label="End by specific date" />
                                        <FormControlLabel value="maxOccurrences" control={<Radio />} label="After X shifts" />
                                    </RadioGroup>
                                </FormControl>
                                <Box sx={{ display: 'flex', gap: 2 }}>
                                    {(formData as any).endCondition === 'endDate' ? (
                                        <DatePicker
                                            label="End Date"
                                            value={formData.recurrenceEndDate ? dayjs(formData.recurrenceEndDate) : null}
                                            onChange={(v) => setFormData({ ...formData, recurrenceEndDate: v ? dayjs(v).format('YYYY-MM-DD') : '' })}
                                            minDate={formData.startDateTime ? dayjs(formData.startDateTime) : dayjs()}
                                            slotProps={{ textField: { sx: { width: '250px' } } }}
                                        />
                                    ) : (
                                        <TextField
                                            sx={{ width: '250px' }}
                                            type="number"
                                            label="Total Number of Shifts"
                                            value={formData.maxOccurrences || 10}
                                            onChange={(e) => setFormData({ ...formData, maxOccurrences: parseInt(e.target.value) || 1 })}
                                            inputProps={{ min: 1, max: 365 }}
                                        />
                                    )}
                                </Box>
                            </Box>
                        </Box>
                    )}

                    {/* Staff Availability */}
                    {formData.staffId && formData.startDateTime && formData.endDateTime && (
                        <Box sx={{ p: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                <Typography variant="subtitle2">Staff Availability Status</Typography>
                                <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={() => checkAvailability(formData.staffId!, formData.startDateTime!, formData.endDateTime!)}
                                    disabled={availabilityLoading}
                                >
                                    {availabilityLoading ? 'Checking…' : 'Refresh'}
                                </Button>
                            </Box>
                            {availabilityLoading ? (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <CircularProgress size={16} />
                                    <Typography variant="body2" color="text.secondary">Checking availability…</Typography>
                                </Box>
                            ) : staffAvailability ? (
                                <Stack spacing={1}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Chip
                                            label={staffAvailability.isAvailable ? 'Available' : 'Not Available'}
                                            color={staffAvailability.isAvailable ? 'success' : 'warning'}
                                            size="small"
                                        />
                                        {selectedStaffProfile && (
                                            <Typography variant="body2" color="text.secondary">
                                                {selectedStaffProfile.firstName} {selectedStaffProfile.lastName}
                                            </Typography>
                                        )}
                                    </Box>
                                    {staffAvailability.conflicts.length > 0 && (
                                        <Box>
                                            <Typography variant="caption" color="warning.main" fontWeight="bold">
                                                Availability Issues:
                                            </Typography>
                                            {staffAvailability.conflicts.map((c, i) => (
                                                <Typography key={i} variant="caption" display="block" color="warning.main">• {c}</Typography>
                                            ))}
                                            {!staffAvailability.isAvailable && canEditShifts && (
                                                <Typography variant="caption" display="block" color="info.main" sx={{ mt: 0.5 }}>
                                                    Note: Administrators can still assign shifts outside of regular availability.
                                                </Typography>
                                            )}
                                        </Box>
                                    )}
                                    {staffAvailability.availability && (
                                        <Typography variant="caption" color="text.secondary">
                                            Regular availability: {staffAvailability.availability.startTime?.slice(0, 5)} - {staffAvailability.availability.endTime?.slice(0, 5)}
                                        </Typography>
                                    )}
                                </Stack>
                            ) : null}
                        </Box>
                    )}

                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <TextField
                            label="Duration (hours)"
                            type="number"
                            fullWidth
                            value={calculatedDuration > 0 ? calculatedDuration.toFixed(2) : (formData.durationHours || 0).toFixed(2)}
                            helperText="Auto-calculated from start and end times"
                            InputProps={{ readOnly: true }}
                            sx={{ '& .MuiInputBase-input': { backgroundColor: 'action.hover' } }}
                        />
                        <TextField
                            label="Travel Kilometres"
                            type="number"
                            fullWidth
                            value={formData.travelKilometres ?? ''}
                            onChange={(e) => {
                                const v = e.target.value;
                                handleChange('travelKilometres', v === '' ? undefined : Math.max(0, parseFloat(v)));
                            }}
                            inputProps={{ min: 0, step: 0.01 }}
                            helperText="Reimbursed to staff and on-charged to client at the configured per-km flat rates."
                        />
                    </Box>

                    {/* Rate Preview */}
                    <Box sx={{ p: 1.5, border: 1, borderColor: 'divider', borderRadius: 1, bgcolor: 'background.default' }}>
                        <Typography variant="subtitle2" gutterBottom>Rate Preview (live, from Billing Rates)</Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                            Hourly rate is no longer entered manually. Pay and charges are calculated from the
                            staff member's StaffBaseRate × StaffPay segments and the client's ClientBaseRate ×
                            ClientCharge segments for the selected day type and time window. Configure these
                            under Billing Rates.
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
                            <Box flex={1} sx={{ p: 1.25, bgcolor: 'background.paper', borderRadius: 1, border: 1, borderColor: 'divider' }}>
                                <Typography variant="caption" color="text.secondary">Staff Pay (estimated)</Typography>
                                <Typography variant="h6" fontWeight={700} sx={{ mb: 0.5 }}>{fmtCurrency(ratePreview.staff.total)}</Typography>
                                {previewStaffBaseRate > 0 && !isSleepover && (
                                    <Typography variant="caption" color="text.secondary" display="block">
                                        Base: {fmtCurrency(previewStaffBaseRate)}/hr
                                    </Typography>
                                )}
                                {ratePreview.staff.segments.map((s, i) => (
                                    <Typography key={i} variant="caption" display="block" color="text.secondary">
                                        • {s.name}: {s.kind === 'Sleepover'
                                            ? `1 night × ${fmtCurrency(s.rate)}`
                                            : `${s.hours.toFixed(2)}h × ${fmtCurrency(s.rate)}/hr`} = {fmtCurrency(s.amount)}
                                    </Typography>
                                ))}
                                {ratePreview.staff.warning && (
                                    <Typography variant="caption" color="warning.main" display="block" sx={{ mt: 0.5 }}>
                                        {ratePreview.staff.warning}
                                    </Typography>
                                )}
                                {previewStaffBaseRate <= 0 && !!formData.staffId && !isSleepover && (
                                    <Typography variant="caption" color="warning.main" display="block">
                                        No active StaffBaseRate. Set one in Billing Rates → Staff Rates.
                                    </Typography>
                                )}
                            </Box>
                            <Box flex={1} sx={{ p: 1.25, bgcolor: 'background.paper', borderRadius: 1, border: 1, borderColor: 'divider' }}>
                                <Typography variant="caption" color="text.secondary">Client Charge (estimated)</Typography>
                                <Typography variant="h6" fontWeight={700} sx={{ mb: 0.5 }}>{fmtCurrency(ratePreview.client.total)}</Typography>
                                {previewClientBaseRate > 0 && !isSleepover && (
                                    <Typography variant="caption" color="text.secondary" display="block">
                                        Base: {fmtCurrency(previewClientBaseRate)}/hr
                                    </Typography>
                                )}
                                {ratePreview.client.segments.map((s, i) => (
                                    <Typography key={i} variant="caption" display="block" color="text.secondary">
                                        • {s.name}: {s.kind === 'Sleepover'
                                            ? `1 night × ${fmtCurrency(s.rate)}`
                                            : `${s.hours.toFixed(2)}h × ${fmtCurrency(s.rate)}/hr`} = {fmtCurrency(s.amount)}
                                    </Typography>
                                ))}
                                {ratePreview.client.warning && (
                                    <Typography variant="caption" color="warning.main" display="block" sx={{ mt: 0.5 }}>
                                        {ratePreview.client.warning}
                                    </Typography>
                                )}
                                {previewClientBaseRate <= 0 && !!formData.clientId && !isSleepover && (
                                    <Typography variant="caption" color="warning.main" display="block">
                                        No active ClientBaseRate. Set one in Billing Rates → Client Rates.
                                    </Typography>
                                )}
                            </Box>
                        </Box>
                    </Box>

                    <TextField
                        label="Care Instructions"
                        fullWidth
                        multiline
                        value={formData.careInstructions || ''}
                        onChange={(e) => handleChange('careInstructions', e.target.value)}
                    />
                    <TextField
                        label="Special Requirements"
                        fullWidth
                        multiline
                        //rows={2}
                        value={formData.specialRequirements || ''}
                        onChange={(e) => handleChange('specialRequirements', e.target.value)}
                    />
                    <TextField
                        label="Work Information"
                        fullWidth
                        multiline
                        rows={4}
                        value={formData.workInformation || ''}
                        onChange={(e) => handleChange('workInformation', e.target.value)}
                        helperText="Summary of work performed during this shift (filled by staff at clock-out)."
                        placeholder="e.g. personal care, meal preparation, community access activities…"
                    />
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={saving}>Cancel</Button>
                <Button onClick={handleSave} variant="contained" disabled={saving}>
                    {saving ? <CircularProgress size={18} /> : (isEditing ? 'Update' : 'Create')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ShiftFormDialog;
